import type { FunctionInterpolation, Interpolation } from "@emotion/react";
import type { MediaSizes } from "@packages/media";
import { usePlayer } from "@packages/media";
import { useMedia } from "react-use";

import {
  numericPercentages,
  numericPixels,
  numericViewportHeight,
  numericViewportWidth,
  radius,
  spacing,
  zIndices,
} from "../../../global/emotion";
import { useDynamicViewport } from "../../hooks/useDynamicViewport";
import {
  cssMediaQueryMaxWidthTinyMobile,
  cssMediaQueryMinHeightNormal,
  cssMediaQueryMinHeightSmall,
  cssMediaQueryMinWidthDesktop,
} from "../../lib/constants/emotionMediaQueries.constants";

//#region Utilities
// determining factors: media type, display mode, screen size, screen orientation
type MediaClass =
  `${"audio" | "video"}-${MediaSizes}-${"portrait" | "landscape"}`;
type MediaInterpolation = Record<MediaClass, Interpolation>;
type MediaFunctionInterpolation<T = any> = Record<
  MediaClass,
  FunctionInterpolation<T>
>;

export const useMediaClass = (): MediaClass => {
  const player = usePlayer();
  const isDesktop = useDynamicViewport({ minimumWidth: 1024 });
  const isPortrait = useMedia("(orientation: portrait)", false);

  return `${player?.mediaType}-${player?.display}-${isDesktop ? "desktop" : "mobile"}-${isPortrait ? "portrait" : "landscape"}`;
};
//#endregion

//#region Core
export const coreVars = {
  videoBackground: "rgba(23,23,23,1.00)",
  catcherInset: spacing.none,
  catcherZIndex: -1,
  innerContainerMobileBottom: spacing.xxxl,
  mobileLowerSectionPlacement: spacing.none,
  mobileLowerSectionPadding: spacing.xl,
  mobileLowerSectionGap: spacing.m,
  mobileLowerSectionWidth: spacing.full,
  fullscreenBtnContainerPlacement: spacing.none,
  fullscreenBtnContainerPadding: spacing.m,
  largeButtonPadding: spacing.none,
  fullscreenScrubberPositionInline: numericPixels[80],
  fullscreenScrubberPositionBottom: spacing.none,
  fullscreenScrubberPaddingBottom: numericPixels[120],
  fullscreenScrubberWidth: "calc(100vw - 160px)",
  playbackButtonSize: numericPixels[80],
  playbackButtonSizeS: numericPixels[40],
  controlGap: spacing.s,
  mediaElementBorderRadius: radius.s,
};

export const coreMediaStyles: Record<string, Interpolation> = {
  bottomEdge: {
    transform: "translateY(100%)",
  },
  centerBase: {
    opacity: 0,
    transition: "opacity 150ms ease-in-out",
  },
  centerVisible: {
    opacity: 1,
  },
  clickCatcher: {
    inset: coreVars.catcherInset,
    // We need this so that we can "disable" the anchor that opens the expanded media player without letting
    // pointer events fall through to whatever is behind the media player
    pointerEvents: "all",
    position: "absolute",
    zIndex: coreVars.catcherZIndex,
  },
  clickCatcherCollapsed: {
    display: "none",
  },
  clickable: {
    pointerEvents: "auto",
  },
  containerTransition: {
    opacity: 0,
  },
  edgeBase: {
    transition: "transform 150ms ease-in-out",
  },
  edgeVisible: {
    transform: "translateY(0)",
  },
  edgeVisibleAbsolute: {
    transform: "translate(-50%, 0)",
  },
  expandedWordButton: {
    transform: "none",
  },
  fullscreenScrubber: {
    bottom: coreVars.fullscreenScrubberPositionBottom,
    left: coreVars.fullscreenScrubberPositionInline,
    paddingBottom: coreVars.fullscreenScrubberPaddingBottom,
    position: "absolute",
    right: coreVars.fullscreenScrubberPositionInline,
    width: coreVars.fullscreenScrubberWidth,
  },
  hideMouse: {
    cursor: "none",
  },
  innerContainer: {
    opacity: 1,
    transition: "opacity 250ms ease-out",
  },
  innerContainerMobile: {
    bottom: coreVars.innerContainerMobileBottom,
  },
  lowerSection: {
    alignItems: "center",
    bottom: coreVars.mobileLowerSectionPlacement,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 0,
    flexWrap: "nowrap",
    gridArea: "controls",
    justifyContent: "space-between",
    padding: coreVars.mobileLowerSectionPadding,
    position: "absolute",
    rowGap: coreVars.mobileLowerSectionGap,
    width: coreVars.mobileLowerSectionWidth,
  },
  lowerSectionLandscape: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    flexWrap: "nowrap",
    gridArea: "controls",
    justifyContent: "center",
    position: "relative",
    rowGap: spacing.m,
    width: spacing.full,
  },
  mediaElementContainer: {
    aspectRatio: 1,
    borderRadius: coreVars.mediaElementBorderRadius,
    display: "flex",
    overflow: "hidden",
    placeContent: "center",
    placeItems: "center",
    position: "relative",
  },
  mediaElementContainerVideo: {
    aspectRatio: "auto",
  },
  outerContainer: {
    WebkitAppearance: "none", // thanks Safari
    display: "block", // this is necessary because it's an anchor element
    opacity: 1,
    transition: "opacity 250ms ease-out",
    zIndex: zIndices.mediaPlayer,
  },
  playbackButton: {
    height: "auto",
    width: "auto",
  },
  playbackButtonL: {
    padding: coreVars.largeButtonPadding,
  },
  playbackButtonPlaceholder: {
    height: coreVars.playbackButtonSize,
    width: coreVars.playbackButtonSize,
  },
  playbackButtonPlaceholderS: {
    height: coreVars.playbackButtonSizeS,
    width: coreVars.playbackButtonSizeS,
  },
  playbackControlSet: {
    alignContent: "center",
    columnGap: coreVars.controlGap,
    display: "grid",
    gridAutoColumns: "min-content",
    gridTemplateAreas: '"back play forward"',
    gridTemplateRows: "1fr",
    placeContent: "end",
    width: spacing.full,
    [cssMediaQueryMinWidthDesktop]: { width: "auto" },
  },
  scrubber: {
    position: "relative",
  },
  standaloneFullscreenButtonContainer: {
    paddingRight: coreVars.fullscreenBtnContainerPadding,
    paddingTop: coreVars.fullscreenBtnContainerPadding,
    position: "fixed",
    right: coreVars.fullscreenBtnContainerPlacement,
    top: coreVars.fullscreenBtnContainerPlacement,
  },
  topEdge: {
    transform: "translateY(-100%)",
  },
  topEdgeAbsolute: {
    transform: "translate(-50%, -100%)",
  },
  unclickable: {
    pointerEvents: "none",
  },
  video: {
    display: "flex",
    flexShrink: 0,
  },
  videoBackground: {
    backgroundColor: coreVars.videoBackground,
  },
};
//#endregion

//#region Outer Container (<a>)
export const outerContainerVars = {
  collapsedDesktopBottom: spacing.l,
  collapsedDesktopLeft: numericPixels[80],
  collapsedDesktopRight: numericPixels[80],
  collapsedDesktopWidth: "calc(100vw - 160px)",
  expandedInset: spacing.none,
};

export const outerContainerStyles: MediaInterpolation = {
  "audio-collapsed-desktop-landscape": {
    bottom: outerContainerVars.collapsedDesktopBottom,
    left: outerContainerVars.collapsedDesktopLeft,
    pointerEvents: "auto",
    position: "fixed",
    right: outerContainerVars.collapsedDesktopRight,
    width: outerContainerVars.collapsedDesktopWidth,
  },
  "audio-collapsed-desktop-portrait": {
    bottom: outerContainerVars.collapsedDesktopBottom,
    left: outerContainerVars.collapsedDesktopLeft,
    pointerEvents: "auto",
    position: "fixed",
    right: outerContainerVars.collapsedDesktopRight,
    width: outerContainerVars.collapsedDesktopWidth,
  },
  "audio-collapsed-mobile-landscape": {
    pointerEvents: "auto",
    position: "fixed",
  },
  "audio-collapsed-mobile-portrait": {
    pointerEvents: "auto",
    position: "fixed",
  },
  "audio-expanded-desktop-landscape": {
    display: "flex",
    inset: outerContainerVars.expandedInset,
    placeContent: "center",
    placeItems: "center",
    pointerEvents: "none",
    position: "fixed",
  },
  "audio-expanded-desktop-portrait": {
    display: "flex",
    inset: outerContainerVars.expandedInset,
    placeContent: "center",
    placeItems: "center",
    pointerEvents: "none",
    position: "fixed",
  },
  "audio-expanded-mobile-landscape": {
    inset: outerContainerVars.expandedInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "audio-expanded-mobile-portrait": {
    inset: outerContainerVars.expandedInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "audio-fullscreen-desktop-landscape": {
    pointerEvents: "none",
  },
  "audio-fullscreen-desktop-portrait": {
    pointerEvents: "none",
  },
  "audio-fullscreen-mobile-landscape": {
    inset: outerContainerVars.expandedInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "audio-fullscreen-mobile-portrait": {
    inset: outerContainerVars.expandedInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "audio-hidden-desktop-landscape": {
    display: "none",
  },
  "audio-hidden-desktop-portrait": {
    display: "none",
  },
  "audio-hidden-mobile-landscape": {
    display: "none",
  },
  "audio-hidden-mobile-portrait": {
    display: "none",
  },
  "video-collapsed-desktop-landscape": {
    bottom: outerContainerVars.collapsedDesktopBottom,
    left: outerContainerVars.collapsedDesktopLeft,
    pointerEvents: "auto",
    position: "fixed",
    right: outerContainerVars.collapsedDesktopRight,
    width: outerContainerVars.collapsedDesktopWidth,
  },
  "video-collapsed-desktop-portrait": {
    bottom: outerContainerVars.collapsedDesktopBottom,
    left: outerContainerVars.collapsedDesktopLeft,
    pointerEvents: "auto",
    position: "fixed",
    right: outerContainerVars.collapsedDesktopRight,
    width: outerContainerVars.collapsedDesktopWidth,
  },
  "video-collapsed-mobile-landscape": {
    pointerEvents: "auto",
    position: "fixed",
  },
  "video-collapsed-mobile-portrait": {
    pointerEvents: "auto",
    position: "fixed",
  },
  "video-expanded-desktop-landscape": {
    display: "flex",
    inset: outerContainerVars.expandedInset,
    placeContent: "center",
    placeItems: "center",
    pointerEvents: "none",
    position: "fixed",
  },
  "video-expanded-desktop-portrait": {
    display: "flex",
    inset: outerContainerVars.expandedInset,
    placeContent: "center",
    placeItems: "center",
    pointerEvents: "none",
    position: "fixed",
  },
  "video-expanded-mobile-landscape": {
    inset: outerContainerVars.expandedInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "video-expanded-mobile-portrait": {
    inset: outerContainerVars.expandedInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "video-fullscreen-desktop-landscape": {
    pointerEvents: "none",
  },
  "video-fullscreen-desktop-portrait": {
    pointerEvents: "none",
  },
  "video-fullscreen-mobile-landscape": {
    inset: outerContainerVars.expandedInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "video-fullscreen-mobile-portrait": {
    inset: outerContainerVars.expandedInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "video-hidden-desktop-landscape": {
    display: "none",
  },
  "video-hidden-desktop-portrait": {
    display: "none",
  },
  "video-hidden-mobile-landscape": {
    display: "none",
  },
  "video-hidden-mobile-portrait": {
    display: "none",
  },
};
//#endregion

//#region Inner Container (<div>)
export const innerContainerVars = {
  collapsedBorderRadius: radius.m,
  collapsedPadding: spacing.s,
  collapsedMobileGap: spacing.s,
  collapsedMobilePosition: spacing.m,
  collapsedMobileWidth: "calc(100vw - 32px)",
  expandedHeight: spacing.full,
  expandedMaxWidthAudio: "1376px", // 1280 + spacing.xxl
  expandedMaxWidthVideo: spacing.full,
  expandedPaddingAudio: spacing.xxl,
  expandedPaddingVideo: spacing.none,
  expandedWidth: spacing.full,
  expandedMobileInset: spacing.none,
};

export const innerContainerStyles: MediaInterpolation = {
  "audio-collapsed-desktop-landscape": {
    borderRadius: innerContainerVars.collapsedBorderRadius,
    padding: innerContainerVars.collapsedPadding,
    position: "relative",
  },
  "audio-collapsed-desktop-portrait": {
    borderRadius: innerContainerVars.collapsedBorderRadius,
    padding: innerContainerVars.collapsedPadding,
    position: "relative",
  },
  "audio-collapsed-mobile-landscape": {
    alignItems: "center",
    borderRadius: innerContainerVars.collapsedBorderRadius,
    bottom: spacing.l,
    columnGap: innerContainerVars.collapsedMobileGap,
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    left: innerContainerVars.collapsedMobilePosition,
    padding: innerContainerVars.collapsedPadding,
    position: "fixed",
    right: innerContainerVars.collapsedMobilePosition,
    width: innerContainerVars.collapsedMobileWidth,
  },
  "audio-collapsed-mobile-portrait": {
    alignItems: "center",
    borderRadius: innerContainerVars.collapsedBorderRadius,
    bottom: spacing.l,
    columnGap: innerContainerVars.collapsedMobileGap,
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    left: innerContainerVars.collapsedMobilePosition,
    padding: innerContainerVars.collapsedPadding,
    position: "fixed",
    right: innerContainerVars.collapsedMobilePosition,
    width: innerContainerVars.collapsedMobileWidth,
  },
  "audio-expanded-desktop-landscape": {
    display: "grid",
    gridTemplateAreas: '"header" "main" "controls"',
    gridTemplateColumns: "1fr",
    gridTemplateRows: "min-content 1fr min-content",
    height: innerContainerVars.expandedHeight,
    maxWidth: innerContainerVars.expandedMaxWidthAudio,
    padding: innerContainerVars.expandedPaddingAudio,
    pointerEvents: "auto",
    width: innerContainerVars.expandedWidth,
  },
  "audio-expanded-desktop-portrait": {
    display: "grid",
    gridTemplateAreas: '"header" "main" "controls"',
    gridTemplateColumns: "1fr",
    gridTemplateRows: "min-content 1fr min-content",
    height: innerContainerVars.expandedHeight,
    maxWidth: innerContainerVars.expandedMaxWidthAudio,
    padding: innerContainerVars.expandedPaddingAudio,
    pointerEvents: "auto",
    width: innerContainerVars.expandedWidth,
  },
  "audio-expanded-mobile-landscape": {
    display: "block",
    inset: innerContainerVars.expandedMobileInset,
    pointerEvents: "auto",
    position: "fixed",
  },
  "audio-expanded-mobile-portrait": {
    display: "block",
    inset: innerContainerVars.expandedMobileInset,
    pointerEvents: "auto",
    position: "fixed",
  },
  "audio-fullscreen-desktop-landscape": {},
  "audio-fullscreen-desktop-portrait": {},
  "audio-fullscreen-mobile-landscape": {},
  "audio-fullscreen-mobile-portrait": {},
  "audio-hidden-desktop-landscape": {
    display: "none",
  },
  "audio-hidden-desktop-portrait": {
    display: "none",
  },
  "audio-hidden-mobile-landscape": {
    display: "none",
  },
  "audio-hidden-mobile-portrait": {
    display: "none",
  },
  "video-collapsed-desktop-landscape": {
    borderRadius: innerContainerVars.collapsedBorderRadius,
    padding: innerContainerVars.collapsedPadding,
    position: "relative",
  },
  "video-collapsed-desktop-portrait": {
    borderRadius: innerContainerVars.collapsedBorderRadius,
    padding: innerContainerVars.collapsedPadding,
    position: "relative",
  },
  "video-collapsed-mobile-landscape": {
    alignItems: "center",
    borderRadius: innerContainerVars.collapsedBorderRadius,
    bottom: spacing.l,
    columnGap: innerContainerVars.collapsedMobileGap,
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    left: innerContainerVars.collapsedMobilePosition,
    padding: innerContainerVars.collapsedPadding,
    position: "fixed",
    right: innerContainerVars.collapsedMobilePosition,
    width: innerContainerVars.collapsedMobileWidth,
  },
  "video-collapsed-mobile-portrait": {
    alignItems: "center",
    borderRadius: innerContainerVars.collapsedBorderRadius,
    bottom: spacing.l,
    columnGap: innerContainerVars.collapsedMobileGap,
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    left: innerContainerVars.collapsedMobilePosition,
    padding: innerContainerVars.collapsedPadding,
    position: "fixed",
    right: innerContainerVars.collapsedMobilePosition,
    width: innerContainerVars.collapsedMobileWidth,
  },
  "video-expanded-desktop-landscape": {
    display: "grid",
    gridTemplateAreas: '"main"',
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    height: innerContainerVars.expandedHeight,
    maxWidth: innerContainerVars.expandedMaxWidthVideo,
    padding: innerContainerVars.expandedPaddingVideo,
    pointerEvents: "auto",
    width: innerContainerVars.expandedWidth,
  },
  "video-expanded-desktop-portrait": {
    display: "grid",
    gridTemplateAreas: '"main"',
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    height: innerContainerVars.expandedHeight,
    maxWidth: innerContainerVars.expandedMaxWidthVideo,
    padding: innerContainerVars.expandedPaddingVideo,
    pointerEvents: "auto",
    width: innerContainerVars.expandedWidth,
  },
  "video-expanded-mobile-landscape": {
    display: "grid",
    gridTemplateAreas: '"main"',
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    height: innerContainerVars.expandedHeight,
    maxWidth: innerContainerVars.expandedMaxWidthVideo,
    padding: innerContainerVars.expandedPaddingVideo,
    pointerEvents: "auto",
    width: innerContainerVars.expandedWidth,
  },
  "video-expanded-mobile-portrait": {
    display: "grid",
    gridTemplateAreas: '"main"',
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    height: innerContainerVars.expandedHeight,
    maxWidth: innerContainerVars.expandedMaxWidthVideo,
    padding: innerContainerVars.expandedPaddingVideo,
    pointerEvents: "auto",
    width: innerContainerVars.expandedWidth,
  },
  "video-fullscreen-desktop-landscape": {
    display: "grid",
    gridTemplateAreas: '"main"',
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    height: innerContainerVars.expandedHeight,
    maxWidth: innerContainerVars.expandedMaxWidthVideo,
    padding: innerContainerVars.expandedPaddingVideo,
    pointerEvents: "auto",
    width: innerContainerVars.expandedWidth,
  },
  "video-fullscreen-desktop-portrait": {
    display: "grid",
    gridTemplateAreas: '"main"',
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    height: innerContainerVars.expandedHeight,
    maxWidth: innerContainerVars.expandedMaxWidthVideo,
    padding: innerContainerVars.expandedPaddingVideo,
    pointerEvents: "auto",
    width: innerContainerVars.expandedWidth,
  },
  "video-fullscreen-mobile-landscape": {
    display: "grid",
    gridTemplateAreas: '"main"',
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    height: innerContainerVars.expandedHeight,
    maxWidth: innerContainerVars.expandedMaxWidthVideo,
    padding: innerContainerVars.expandedPaddingVideo,
    pointerEvents: "auto",
    width: innerContainerVars.expandedWidth,
  },
  "video-fullscreen-mobile-portrait": {
    display: "grid",
    gridTemplateAreas: '"main"',
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    height: innerContainerVars.expandedHeight,
    maxWidth: innerContainerVars.expandedMaxWidthVideo,
    padding: innerContainerVars.expandedPaddingVideo,
    pointerEvents: "auto",
    width: innerContainerVars.expandedWidth,
  },
  "video-hidden-desktop-landscape": {
    display: "none",
  },
  "video-hidden-desktop-portrait": {
    display: "none",
  },
  "video-hidden-mobile-landscape": {
    display: "none",
  },
  "video-hidden-mobile-portrait": {
    display: "none",
  },
};
//#endregion

//#region Media Container (holds info, elements, and most controls)
export const mediaContainerVars = {
  collapsedDesktopGap: spacing.m,
  collapsedMobileGap: spacing.s,
  collapsedMobileWidth: spacing.full,
  expandedDesktopMaxWidthAudio: numericPixels[480],
  expandedMaxWidthVideo: numericViewportWidth[100],
  expandedWidthVideo: spacing.full,
  expandedGap: spacing.ml,
  expandedMobileWidthPortraitAudio: numericViewportWidth[100],
  expandedMobileHeightPortraitAudio: numericViewportHeight[100],
  expandedMobileInset: spacing.none,
};

export const mediaContainerStyles: MediaInterpolation = {
  "audio-collapsed-desktop-landscape": {
    alignItems: "center",
    columnGap: mediaContainerVars.collapsedDesktopGap,
    display: "grid",
    gridTemplateAreas: '"info playback settings"',
    gridTemplateColumns: "1fr max-content 1fr",
    gridTemplateRows: "1fr",
  },
  "audio-collapsed-desktop-portrait": {
    alignItems: "center",
    columnGap: mediaContainerVars.collapsedDesktopGap,
    display: "grid",
    gridTemplateAreas: '"info playback settings"',
    gridTemplateColumns: "1fr max-content 1fr",
    gridTemplateRows: "1fr",
  },
  "audio-collapsed-mobile-landscape": {
    alignItems: "center",
    columnGap: mediaContainerVars.collapsedMobileGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "space-between",
    width: mediaContainerVars.collapsedMobileWidth,
  },
  "audio-collapsed-mobile-portrait": {
    alignItems: "center",
    columnGap: mediaContainerVars.collapsedMobileGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "space-between",
    width: mediaContainerVars.collapsedMobileWidth,
  },
  "audio-expanded-desktop-landscape": {
    display: "flex",
    flexDirection: "column",
    gridArea: "main",
    justifySelf: "center",
    maxWidth: mediaContainerVars.expandedDesktopMaxWidthAudio,
    placeContent: "center",
    placeItems: "center",
    position: "relative",
    rowGap: mediaContainerVars.expandedGap,
  },
  "audio-expanded-desktop-portrait": {
    display: "flex",
    flexDirection: "column",
    gridArea: "main",
    justifySelf: "center",
    maxWidth: mediaContainerVars.expandedDesktopMaxWidthAudio,
    placeContent: "center",
    placeItems: "center",
    position: "relative",
    rowGap: mediaContainerVars.expandedGap,
  },
  "audio-expanded-mobile-landscape": {
    columnGap: spacing.xxl,
    [cssMediaQueryMaxWidthTinyMobile]: {
      columnGap: spacing.l,
      paddingInline: spacing.xxl,
    },
    display: "grid",
    gridTemplateAreas: "'illo controls'",
    gridTemplateColumns: "50vmin 1fr",
    gridTemplateRows: "1fr",
    paddingBlock: spacing.l,
    paddingInline: numericPixels[80],
  },
  "audio-expanded-mobile-portrait": {
    display: "block",
    height: mediaContainerVars.expandedMobileHeightPortraitAudio,
    inset: mediaContainerVars.expandedMobileInset,
    position: "fixed",
    width: mediaContainerVars.expandedMobileWidthPortraitAudio,
  },
  "audio-fullscreen-desktop-landscape": {},
  "audio-fullscreen-desktop-portrait": {},
  "audio-fullscreen-mobile-landscape": {},
  "audio-fullscreen-mobile-portrait": {},
  "audio-hidden-desktop-landscape": {},
  "audio-hidden-desktop-portrait": {},
  "audio-hidden-mobile-landscape": {},
  "audio-hidden-mobile-portrait": {},
  "video-collapsed-desktop-landscape": {
    alignItems: "center",
    columnGap: mediaContainerVars.collapsedDesktopGap,
    display: "grid",
    gridTemplateAreas: '"info playback settings"',
    gridTemplateColumns: "1fr max-content 1fr",
    gridTemplateRows: "1fr",
  },
  "video-collapsed-desktop-portrait": {
    alignItems: "center",
    columnGap: mediaContainerVars.collapsedDesktopGap,
    display: "grid",
    gridTemplateAreas: '"info playback settings"',
    gridTemplateColumns: "1fr max-content 1fr",
    gridTemplateRows: "1fr",
  },
  "video-collapsed-mobile-landscape": {
    alignItems: "center",
    columnGap: mediaContainerVars.collapsedMobileGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "space-between",
    width: mediaContainerVars.collapsedMobileWidth,
  },
  "video-collapsed-mobile-portrait": {
    alignItems: "center",
    columnGap: mediaContainerVars.collapsedMobileGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "space-between",
    width: mediaContainerVars.collapsedMobileWidth,
  },
  "video-expanded-desktop-landscape": {
    display: "flex",
    flexDirection: "column",
    justifySelf: "center",
    maxWidth: mediaContainerVars.expandedMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "relative",
    rowGap: mediaContainerVars.expandedGap,
    width: mediaContainerVars.expandedWidthVideo,
  },
  "video-expanded-desktop-portrait": {
    display: "flex",
    flexDirection: "column",
    justifySelf: "center",
    maxWidth: mediaContainerVars.expandedMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "relative",
    rowGap: mediaContainerVars.expandedGap,
    width: mediaContainerVars.expandedWidthVideo,
  },
  "video-expanded-mobile-landscape": {
    display: "flex",
    flexDirection: "column",
    justifySelf: "center",
    maxWidth: mediaContainerVars.expandedMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "relative",
    rowGap: mediaContainerVars.expandedGap,
    width: mediaContainerVars.expandedWidthVideo,
  },
  "video-expanded-mobile-portrait": {
    display: "flex",
    flexDirection: "column",
    justifySelf: "center",
    maxWidth: mediaContainerVars.expandedMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "relative",
    rowGap: mediaContainerVars.expandedGap,
    width: mediaContainerVars.expandedWidthVideo,
  },
  "video-fullscreen-desktop-landscape": {
    display: "flex",
    flexDirection: "column",
    justifySelf: "center",
    maxWidth: mediaContainerVars.expandedMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "relative",
    rowGap: mediaContainerVars.expandedGap,
    width: mediaContainerVars.expandedWidthVideo,
  },
  "video-fullscreen-desktop-portrait": {
    display: "flex",
    flexDirection: "column",
    justifySelf: "center",
    maxWidth: mediaContainerVars.expandedMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "relative",
    rowGap: mediaContainerVars.expandedGap,
    width: mediaContainerVars.expandedWidthVideo,
  },
  "video-fullscreen-mobile-landscape": {
    display: "flex",
    flexDirection: "column",
    justifySelf: "center",
    maxWidth: mediaContainerVars.expandedMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "relative",
    rowGap: mediaContainerVars.expandedGap,
    width: mediaContainerVars.expandedWidthVideo,
  },
  "video-fullscreen-mobile-portrait": {
    display: "flex",
    flexDirection: "column",
    justifySelf: "center",
    maxWidth: mediaContainerVars.expandedMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "relative",
    rowGap: mediaContainerVars.expandedGap,
    width: mediaContainerVars.expandedWidthVideo,
  },
  "video-hidden-desktop-landscape": {},
  "video-hidden-desktop-portrait": {},
  "video-hidden-mobile-landscape": {},
  "video-hidden-mobile-portrait": {},
};
//#endregion

//#region Media (div immediately around elements)
export const mediaVars = {
  // we're intentionally assigning a "width" variable to a "height" value
  expandedDesktopHeightAudio: mediaContainerVars.expandedDesktopMaxWidthAudio,
  expandedDesktopHeightTiny: spacing.none,
  expandedDesktopWidth: spacing.full,
  expandedDesktopWidthShort: numericPercentages[50],
  expandedMobileHeightPortraitAudio: "calc(100vh - 260px)",
  expandedMobileHeightLandscapeAudio: "50svmin",
  expandedMobileWidthLandscapeAudio: "50svmin",
  expandedMobileHeightVideo: numericViewportHeight[100],
  expandedMobileWidth: spacing.full,
  expandedMobileMargin: spacing.none,
  expandedMobileTop: spacing.none,
  expandedMobileZIndex: -1,
  expandedMobileMaxWidthVideo: numericViewportWidth[100],
};

export const mediaStyles: MediaInterpolation = {
  "audio-collapsed-desktop-landscape": {
    gridArea: "info",
  },
  "audio-collapsed-desktop-portrait": {
    gridArea: "info",
  },
  "audio-collapsed-mobile-landscape": {},
  "audio-collapsed-mobile-portrait": {},
  "audio-expanded-desktop-landscape": {
    height: mediaVars.expandedDesktopHeightTiny,
    [cssMediaQueryMinHeightNormal]: {
      height: mediaVars.expandedDesktopHeightAudio,
      width: mediaVars.expandedDesktopWidth,
    },
    [cssMediaQueryMinHeightSmall]: { height: "auto" },
    width: mediaVars.expandedDesktopWidthShort,
  },
  "audio-expanded-desktop-portrait": {
    height: mediaVars.expandedDesktopHeightTiny,
    [cssMediaQueryMinHeightNormal]: {
      height: mediaVars.expandedDesktopHeightAudio,
      width: mediaVars.expandedDesktopWidth,
    },
    [cssMediaQueryMinHeightSmall]: {
      height: "auto",
      width: mediaVars.expandedDesktopWidth,
    },
    width: mediaVars.expandedDesktopWidthShort,
  },
  "audio-expanded-mobile-landscape": {
    gridArea: "illo",
    height: mediaVars.expandedMobileHeightLandscapeAudio,
    placeContent: "center",
    placeItems: "center",
    width: mediaVars.expandedMobileWidthLandscapeAudio,
  },
  "audio-expanded-mobile-portrait": {
    height: mediaVars.expandedMobileHeightPortraitAudio,
    margin: mediaVars.expandedMobileMargin,
    placeContent: "center",
    placeItems: "center",
    position: "absolute",
    top: mediaVars.expandedMobileTop,
    width: mediaVars.expandedMobileWidth,
    zIndex: mediaVars.expandedMobileZIndex,
  },
  "audio-fullscreen-desktop-landscape": {},
  "audio-fullscreen-desktop-portrait": {},
  "audio-fullscreen-mobile-landscape": {},
  "audio-fullscreen-mobile-portrait": {},
  "audio-hidden-desktop-landscape": {},
  "audio-hidden-desktop-portrait": {},
  "audio-hidden-mobile-landscape": {},
  "audio-hidden-mobile-portrait": {},
  "video-collapsed-desktop-landscape": {
    gridArea: "info",
  },
  "video-collapsed-desktop-portrait": {
    gridArea: "info",
  },
  "video-collapsed-mobile-landscape": {},
  "video-collapsed-mobile-portrait": {},
  "video-expanded-desktop-landscape": {},
  "video-expanded-desktop-portrait": {},
  "video-expanded-mobile-landscape": {
    height: mediaVars.expandedMobileHeightVideo,
    margin: mediaVars.expandedMobileMargin,
    maxWidth: mediaVars.expandedMobileMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "absolute",
    top: mediaVars.expandedMobileTop,
    width: mediaVars.expandedMobileWidth,
  },
  "video-expanded-mobile-portrait": {
    height: mediaVars.expandedMobileHeightVideo,
    margin: mediaVars.expandedMobileMargin,
    maxWidth: mediaVars.expandedMobileMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "absolute",
    top: mediaVars.expandedMobileTop,
    width: mediaVars.expandedMobileWidth,
  },
  "video-fullscreen-desktop-landscape": {},
  "video-fullscreen-desktop-portrait": {},
  "video-fullscreen-mobile-landscape": {
    height: mediaVars.expandedMobileHeightVideo,
    margin: mediaVars.expandedMobileMargin,
    maxWidth: mediaVars.expandedMobileMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "absolute",
    top: mediaVars.expandedMobileTop,
    width: mediaVars.expandedMobileWidth,
  },
  "video-fullscreen-mobile-portrait": {
    height: mediaVars.expandedMobileHeightVideo,
    margin: mediaVars.expandedMobileMargin,
    maxWidth: mediaVars.expandedMobileMaxWidthVideo,
    placeContent: "center",
    placeItems: "center",
    position: "absolute",
    top: mediaVars.expandedMobileTop,
    width: mediaVars.expandedMobileWidth,
  },
  "video-hidden-desktop-landscape": {},
  "video-hidden-desktop-portrait": {},
  "video-hidden-mobile-landscape": {},
  "video-hidden-mobile-portrait": {},
};
//#endregion

//#region Info
export const infoVars = {
  collapsedDesktopAudioPaddingLeft: numericPixels[80],
  expandedDesktopAudioMarginTop: numericPixels[4], // difference between ml and l for row gaps
  expandedWidth: spacing.full,
  expandedLandscapeVideoPlacement: spacing.none,
  expandedDesktopVideoPaddingBlock: spacing.xxl,
  expandedVideoPaddingInline: numericPixels[80],
  expandedMobileVideoPaddingBlock: spacing.l,
};
const IMAGE_TO_TEXT_GAP = 16;

export const infoStyles: MediaFunctionInterpolation<{ videoWidth?: number }> = {
  "audio-collapsed-desktop-landscape": () => ({
    gridArea: "info",
    paddingLeft: infoVars.collapsedDesktopAudioPaddingLeft,
  }),
  "audio-collapsed-desktop-portrait": () => ({
    gridArea: "info",
    paddingLeft: infoVars.collapsedDesktopAudioPaddingLeft,
  }),
  "audio-collapsed-mobile-landscape": () => ({
    flexGrow: 1,
    flexShrink: 1,
  }),
  "audio-collapsed-mobile-portrait": () => ({
    flexGrow: 1,
    flexShrink: 1,
  }),
  "audio-expanded-desktop-landscape": () => ({
    marginTop: infoVars.expandedDesktopAudioMarginTop,
    width: infoVars.expandedWidth,
  }),
  "audio-expanded-desktop-portrait": () => ({
    marginTop: infoVars.expandedDesktopAudioMarginTop,
    width: infoVars.expandedWidth,
  }),
  "audio-expanded-mobile-landscape": () => ({
    width: infoVars.expandedWidth,
  }),
  "audio-expanded-mobile-portrait": () => ({
    width: infoVars.expandedWidth,
  }),
  "audio-fullscreen-desktop-landscape": () => ({}),
  "audio-fullscreen-desktop-portrait": () => ({}),
  "audio-fullscreen-mobile-landscape": () => ({}),
  "audio-fullscreen-mobile-portrait": () => ({}),
  "audio-hidden-desktop-landscape": () => ({}),
  "audio-hidden-desktop-portrait": () => ({}),
  "audio-hidden-mobile-landscape": () => ({}),
  "audio-hidden-mobile-portrait": () => ({}),
  "video-collapsed-desktop-landscape": (props) => ({
    gridArea: "info",
    paddingLeft: props.videoWidth + IMAGE_TO_TEXT_GAP,
  }),
  "video-collapsed-desktop-portrait": (props) => ({
    gridArea: "info",
    paddingLeft: props.videoWidth + IMAGE_TO_TEXT_GAP,
  }),
  "video-collapsed-mobile-landscape": () => ({
    flexGrow: 1,
    flexShrink: 1,
  }),
  "video-collapsed-mobile-portrait": () => ({
    flexGrow: 1,
    flexShrink: 1,
  }),
  "video-expanded-desktop-landscape": () => ({
    left: infoVars.expandedLandscapeVideoPlacement,
    paddingBlock: infoVars.expandedDesktopVideoPaddingBlock,
    paddingInline: infoVars.expandedVideoPaddingInline,
    position: "fixed",
    right: infoVars.expandedLandscapeVideoPlacement,
    top: infoVars.expandedLandscapeVideoPlacement,
    width: infoVars.expandedWidth,
  }),
  "video-expanded-desktop-portrait": () => ({
    left: infoVars.expandedLandscapeVideoPlacement,
    paddingBlock: infoVars.expandedDesktopVideoPaddingBlock,
    paddingInline: infoVars.expandedVideoPaddingInline,
    position: "fixed",
    right: infoVars.expandedLandscapeVideoPlacement,
    top: infoVars.expandedLandscapeVideoPlacement,
    width: infoVars.expandedWidth,
  }),
  "video-expanded-mobile-landscape": () => ({
    left: infoVars.expandedLandscapeVideoPlacement,
    paddingBlock: infoVars.expandedMobileVideoPaddingBlock,
    paddingInline: infoVars.expandedVideoPaddingInline,
    position: "fixed",
    right: infoVars.expandedLandscapeVideoPlacement,
    top: infoVars.expandedLandscapeVideoPlacement,
    width: infoVars.expandedWidth,
  }),
  "video-expanded-mobile-portrait": () => ({
    flexGrow: 1,
    flexShrink: 1,
    width: infoVars.expandedWidth,
  }),
  "video-fullscreen-desktop-landscape": () => ({
    left: infoVars.expandedLandscapeVideoPlacement,
    paddingBlock: infoVars.expandedDesktopVideoPaddingBlock,
    paddingInline: infoVars.expandedVideoPaddingInline,
    position: "fixed",
    right: infoVars.expandedLandscapeVideoPlacement,
    top: infoVars.expandedLandscapeVideoPlacement,
    width: infoVars.expandedWidth,
  }),
  "video-fullscreen-desktop-portrait": () => ({
    left: infoVars.expandedLandscapeVideoPlacement,
    paddingBlock: infoVars.expandedDesktopVideoPaddingBlock,
    paddingInline: infoVars.expandedVideoPaddingInline,
    position: "fixed",
    right: infoVars.expandedLandscapeVideoPlacement,
    top: infoVars.expandedLandscapeVideoPlacement,
    width: infoVars.expandedWidth,
  }),
  "video-fullscreen-mobile-landscape": () => ({
    left: infoVars.expandedLandscapeVideoPlacement,
    paddingBlock: infoVars.expandedMobileVideoPaddingBlock,
    paddingInline: infoVars.expandedVideoPaddingInline,
    position: "fixed",
    right: infoVars.expandedLandscapeVideoPlacement,
    top: infoVars.expandedLandscapeVideoPlacement,
    width: infoVars.expandedWidth,
  }),
  "video-fullscreen-mobile-portrait": () => ({
    flexGrow: 1,
    flexShrink: 1,
    width: infoVars.expandedWidth,
  }),
  "video-hidden-desktop-landscape": () => ({}),
  "video-hidden-desktop-portrait": () => ({}),
  "video-hidden-mobile-landscape": () => ({}),
  "video-hidden-mobile-portrait": () => ({}),
};
//#endregion

//#region Media Controls Container
export const playerControlsContainerVars = {
  collapsedGap: spacing.xxs,
  expandedGap: spacing.ml,
  expandedWidth: spacing.full,
  expandedVideoLandscapeInset: spacing.none,
};

export const playerControlsContainerStyles: MediaInterpolation = {
  "audio-collapsed-desktop-landscape": {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    gridArea: "playback",
    justifyContent: "center",
    rowGap: playerControlsContainerVars.collapsedGap,
  },
  "audio-collapsed-desktop-portrait": {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    gridArea: "playback",
    justifyContent: "center",
    rowGap: playerControlsContainerVars.collapsedGap,
  },
  "audio-collapsed-mobile-landscape": {},
  "audio-collapsed-mobile-portrait": {},
  "audio-expanded-desktop-landscape": {
    width: playerControlsContainerVars.expandedWidth,
  },
  "audio-expanded-desktop-portrait": {
    width: playerControlsContainerVars.expandedWidth,
  },
  "audio-expanded-mobile-landscape": {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    gridArea: "controls",
    justifyContent: "flex-start",
    rowGap: playerControlsContainerVars.expandedGap,
    width: playerControlsContainerVars.expandedWidth,
  },
  "audio-expanded-mobile-portrait": {},
  "audio-fullscreen-desktop-landscape": {},
  "audio-fullscreen-desktop-portrait": {},
  "audio-fullscreen-mobile-landscape": {},
  "audio-fullscreen-mobile-portrait": {},
  "audio-hidden-desktop-landscape": {},
  "audio-hidden-desktop-portrait": {},
  "audio-hidden-mobile-landscape": {},
  "audio-hidden-mobile-portrait": {},
  "video-collapsed-desktop-landscape": {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    gridArea: "playback",
    justifyContent: "center",
    rowGap: playerControlsContainerVars.collapsedGap,
  },
  "video-collapsed-desktop-portrait": {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    gridArea: "playback",
    justifyContent: "center",
    rowGap: playerControlsContainerVars.collapsedGap,
  },
  "video-collapsed-mobile-landscape": {},
  "video-collapsed-mobile-portrait": {},
  "video-expanded-desktop-landscape": {
    inset: playerControlsContainerVars.expandedVideoLandscapeInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "video-expanded-desktop-portrait": {
    inset: playerControlsContainerVars.expandedVideoLandscapeInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "video-expanded-mobile-landscape": {
    inset: playerControlsContainerVars.expandedVideoLandscapeInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "video-expanded-mobile-portrait": {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    justifyContent: "flex-start",
    rowGap: playerControlsContainerVars.expandedGap,
    width: playerControlsContainerVars.expandedWidth,
  },
  "video-fullscreen-desktop-landscape": {
    inset: playerControlsContainerVars.expandedVideoLandscapeInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "video-fullscreen-desktop-portrait": {
    inset: playerControlsContainerVars.expandedVideoLandscapeInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "video-fullscreen-mobile-landscape": {
    inset: playerControlsContainerVars.expandedVideoLandscapeInset,
    pointerEvents: "none",
    position: "fixed",
  },
  "video-fullscreen-mobile-portrait": {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    justifyContent: "flex-start",
    rowGap: playerControlsContainerVars.expandedGap,
    width: playerControlsContainerVars.expandedWidth,
  },
  "video-hidden-desktop-landscape": {},
  "video-hidden-desktop-portrait": {},
  "video-hidden-mobile-landscape": {},
  "video-hidden-mobile-portrait": {},
};
//#endregion

//#region Media Controls Button
export const playerButtonVars = {
  desktopOrExpandedGap: spacing.l,
  desktopPadding: spacing.s,
  expandedMobileWidth: spacing.full,
  expandedMobileLandscapeHeight: spacing.full,
};

export const playerButtonStyles: MediaInterpolation = {
  "audio-collapsed-desktop-landscape": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    padding: playerButtonVars.desktopPadding,
  },
  "audio-collapsed-desktop-portrait": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    padding: playerButtonVars.desktopPadding,
  },
  "audio-collapsed-mobile-landscape": {},
  "audio-collapsed-mobile-portrait": {},
  "audio-expanded-desktop-landscape": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    padding: playerButtonVars.desktopPadding,
  },
  "audio-expanded-desktop-portrait": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    padding: playerButtonVars.desktopPadding,
  },
  "audio-expanded-mobile-landscape": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    width: playerButtonVars.expandedMobileWidth,
  },
  "audio-expanded-mobile-portrait": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    width: playerButtonVars.expandedMobileWidth,
  },
  "audio-fullscreen-desktop-landscape": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
  },
  "audio-fullscreen-desktop-portrait": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
  },
  "audio-fullscreen-mobile-landscape": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    width: playerButtonVars.expandedMobileWidth,
  },
  "audio-fullscreen-mobile-portrait": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    width: playerButtonVars.expandedMobileWidth,
  },
  "audio-hidden-desktop-landscape": {},
  "audio-hidden-desktop-portrait": {},
  "audio-hidden-mobile-landscape": {},
  "audio-hidden-mobile-portrait": {},
  "video-collapsed-desktop-landscape": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    padding: playerButtonVars.desktopPadding,
  },
  "video-collapsed-desktop-portrait": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    padding: playerButtonVars.desktopPadding,
  },
  "video-collapsed-mobile-landscape": {
    pointerEvents: "none",
  },
  "video-collapsed-mobile-portrait": {},
  "video-expanded-desktop-landscape": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    padding: playerButtonVars.desktopPadding,
  },
  "video-expanded-desktop-portrait": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    padding: playerButtonVars.desktopPadding,
  },
  "video-expanded-mobile-landscape": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    height: playerButtonVars.expandedMobileLandscapeHeight,
    justifyContent: "center",
    width: playerButtonVars.expandedMobileWidth,
  },
  "video-expanded-mobile-portrait": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    width: playerButtonVars.expandedMobileWidth,
  },
  "video-fullscreen-desktop-landscape": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
  },
  "video-fullscreen-desktop-portrait": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
  },
  "video-fullscreen-mobile-landscape": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    width: playerButtonVars.expandedMobileWidth,
  },
  "video-fullscreen-mobile-portrait": {
    alignItems: "center",
    columnGap: playerButtonVars.desktopOrExpandedGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    width: playerButtonVars.expandedMobileWidth,
  },
  "video-hidden-desktop-landscape": {},
  "video-hidden-desktop-portrait": {},
  "video-hidden-mobile-landscape": {},
  "video-hidden-mobile-portrait": {},
};
//#endregion

//#region Playback Controls Container
export const playbackCtlContainerVars = {
  fullscreenGap: spacing.xxxl,
  fullscreenWidth: numericViewportWidth[40],
  mobileGap: spacing.none,
  collapsedDesktopGap: spacing.l,
  expandedDesktopGap: spacing.xxl,
  fullscreenMobileGap: spacing.xl,
};

export const playbackCtlContainerStyles: MediaInterpolation = {
  "audio-collapsed-desktop-landscape": {
    columnGap: playbackCtlContainerVars.collapsedDesktopGap,
  },
  "audio-collapsed-desktop-portrait": {
    columnGap: playbackCtlContainerVars.collapsedDesktopGap,
  },
  "audio-collapsed-mobile-landscape": {
    columnGap: playbackCtlContainerVars.mobileGap,
    gridTemplateAreas: '"play close"',
  },
  "audio-collapsed-mobile-portrait": {
    columnGap: playbackCtlContainerVars.mobileGap,
    gridTemplateAreas: '"play close"',
  },
  "audio-expanded-desktop-landscape": {
    columnGap: playbackCtlContainerVars.expandedDesktopGap,
  },
  "audio-expanded-desktop-portrait": {
    columnGap: playbackCtlContainerVars.expandedDesktopGap,
  },
  "audio-expanded-mobile-landscape": {
    justifyContent: "space-between",
  },
  "audio-expanded-mobile-portrait": {
    justifyContent: "space-between",
  },
  "audio-fullscreen-desktop-landscape": {},
  "audio-fullscreen-desktop-portrait": {},
  "audio-fullscreen-mobile-landscape": {},
  "audio-fullscreen-mobile-portrait": {},
  "audio-hidden-desktop-landscape": {},
  "audio-hidden-desktop-portrait": {},
  "audio-hidden-mobile-landscape": {},
  "audio-hidden-mobile-portrait": {},
  "video-collapsed-desktop-landscape": {
    columnGap: playbackCtlContainerVars.collapsedDesktopGap,
  },
  "video-collapsed-desktop-portrait": {
    columnGap: playbackCtlContainerVars.collapsedDesktopGap,
  },
  "video-collapsed-mobile-landscape": {
    columnGap: playbackCtlContainerVars.mobileGap,
    gridTemplateAreas: '"play close"',
  },
  "video-collapsed-mobile-portrait": {
    columnGap: playbackCtlContainerVars.mobileGap,
    gridTemplateAreas: '"play close"',
  },
  "video-expanded-desktop-landscape": {
    columnGap: playbackCtlContainerVars.fullscreenGap,
    display: "flex",
    justifyContent: "space-between",
    left: numericPercentages[50],
    position: "absolute",
    top: numericPercentages[50],
    transform: "translate(-50%, -50%)",
    width: playbackCtlContainerVars.fullscreenWidth,
  },
  "video-expanded-desktop-portrait": {
    columnGap: playbackCtlContainerVars.fullscreenGap,
    display: "flex",
    justifyContent: "space-between",
    left: numericPercentages[50],
    position: "absolute",
    top: numericPercentages[50],
    transform: "translate(-50%, -50%)",
    width: playbackCtlContainerVars.fullscreenWidth,
  },
  "video-expanded-mobile-landscape": {
    columnGap: playbackCtlContainerVars.fullscreenMobileGap,
    display: "flex",
    justifyContent: "space-between",
    left: numericPercentages[50],
    pointerEvents: "none",
    position: "absolute",
    top: numericPercentages[50],
    transform: "translate(-50%, -50%)",
    width: "min-content",
  },
  "video-expanded-mobile-portrait": {
    justifyContent: "space-between",
  },
  "video-fullscreen-desktop-landscape": {
    columnGap: playbackCtlContainerVars.fullscreenGap,
    display: "flex",
    justifyContent: "space-between",
    left: numericPercentages[50],
    position: "absolute",
    top: numericPercentages[50],
    transform: "translate(-50%, -50%)",
    width: playbackCtlContainerVars.fullscreenWidth,
  },
  "video-fullscreen-desktop-portrait": {
    columnGap: playbackCtlContainerVars.fullscreenGap,
    display: "flex",
    justifyContent: "space-between",
    left: numericPercentages[50],
    position: "absolute",
    top: numericPercentages[50],
    transform: "translate(-50%, -50%)",
    width: playbackCtlContainerVars.fullscreenWidth,
  },
  "video-fullscreen-mobile-landscape": {
    columnGap: playbackCtlContainerVars.fullscreenMobileGap,
    display: "flex",
    justifyContent: "space-between",
    left: numericPercentages[50],
    position: "absolute",
    top: numericPercentages[50],
    transform: "translate(-50%, -50%)",
    width: playbackCtlContainerVars.fullscreenWidth,
  },
  "video-fullscreen-mobile-portrait": {
    justifyContent: "space-between",
  },
  "video-hidden-desktop-landscape": {},
  "video-hidden-desktop-portrait": {},
  "video-hidden-mobile-landscape": {},
  "video-hidden-mobile-portrait": {},
};
//#endregion

//#region Settings Container Styles
export const settingsContainerVars = {
  desktopColumnGap: spacing.s,
  desktopPaddingInline: spacing.m,
  desktopRowGap: spacing.m,
  videoPosition: spacing.none,
  videoDesktopPadding: numericPixels[80],
  mobileWidth: spacing.full,
};

export const settingsContainerStyles: MediaInterpolation = {
  "audio-collapsed-desktop-landscape": {
    alignItems: "center",
    columnGap: settingsContainerVars.desktopColumnGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gridArea: "settings",
    justifyContent: "flex-end",
    paddingInline: settingsContainerVars.desktopPaddingInline,
  },
  "audio-collapsed-desktop-portrait": {
    alignItems: "center",
    columnGap: settingsContainerVars.desktopColumnGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gridArea: "settings",
    justifyContent: "flex-end",
    paddingInline: settingsContainerVars.desktopPaddingInline,
  },
  "audio-collapsed-mobile-landscape": {
    display: "none",
  },
  "audio-collapsed-mobile-portrait": {
    display: "none",
  },
  "audio-expanded-desktop-landscape": {
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    gridArea: "controls",
    rowGap: settingsContainerVars.desktopRowGap,
  },
  "audio-expanded-desktop-portrait": {
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    gridArea: "controls",
    rowGap: settingsContainerVars.desktopRowGap,
  },
  "audio-expanded-mobile-landscape": {
    paddingInline: numericPixels[80],
    [cssMediaQueryMaxWidthTinyMobile]: { paddingInline: spacing.xxl },
  },
  "audio-expanded-mobile-portrait": {},
  "audio-fullscreen-desktop-landscape": {},
  "audio-fullscreen-desktop-portrait": {},
  "audio-fullscreen-mobile-landscape": {},
  "audio-fullscreen-mobile-portrait": {},
  "audio-hidden-desktop-landscape": {},
  "audio-hidden-desktop-portrait": {},
  "audio-hidden-mobile-landscape": {},
  "audio-hidden-mobile-portrait": {},
  "video-collapsed-desktop-landscape": {
    alignItems: "center",
    columnGap: settingsContainerVars.desktopColumnGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gridArea: "settings",
    justifyContent: "flex-end",
    paddingInline: settingsContainerVars.desktopPaddingInline,
  },
  "video-collapsed-desktop-portrait": {
    alignItems: "center",
    columnGap: settingsContainerVars.desktopColumnGap,
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gridArea: "settings",
    justifyContent: "flex-end",
    paddingInline: settingsContainerVars.desktopPaddingInline,
  },
  "video-collapsed-mobile-landscape": {
    display: "none",
  },
  "video-collapsed-mobile-portrait": {
    display: "none",
  },
  "video-expanded-desktop-landscape": {
    bottom: settingsContainerVars.videoPosition,
    left: settingsContainerVars.videoPosition,
    padding: settingsContainerVars.videoDesktopPadding,
    pointerEvents: "none",
    position: "absolute",
    right: settingsContainerVars.videoPosition,
  },
  "video-expanded-desktop-portrait": {
    bottom: settingsContainerVars.videoPosition,
    left: settingsContainerVars.videoPosition,
    padding: settingsContainerVars.videoDesktopPadding,
    pointerEvents: "none",
    position: "absolute",
    right: settingsContainerVars.videoPosition,
  },
  "video-expanded-mobile-landscape": {
    display: "none",
  },
  "video-expanded-mobile-portrait": {
    pointerEvents: "none",
    width: settingsContainerVars.mobileWidth,
  },
  "video-fullscreen-desktop-landscape": {
    bottom: settingsContainerVars.videoPosition,
    left: settingsContainerVars.videoPosition,
    padding: settingsContainerVars.videoDesktopPadding,
    position: "absolute",
    right: settingsContainerVars.videoPosition,
  },
  "video-fullscreen-desktop-portrait": {
    bottom: settingsContainerVars.videoPosition,
    left: settingsContainerVars.videoPosition,
    padding: settingsContainerVars.videoDesktopPadding,
    position: "absolute",
    right: settingsContainerVars.videoPosition,
  },
  "video-fullscreen-mobile-landscape": {
    display: "none",
  },
  "video-fullscreen-mobile-portrait": {
    width: settingsContainerVars.mobileWidth,
  },
  "video-hidden-desktop-landscape": {},
  "video-hidden-desktop-portrait": {},
  "video-hidden-mobile-landscape": {},
  "video-hidden-mobile-portrait": {},
};
//#endregion

//#region Collapse Button Container styles
export const collapseHeaderVars = {
  zIndex: zIndices[1],
  mobileWidth: spacing.full,
  expandedLeft: numericPercentages[50],
  expandedTop: spacing.none,
  expandedDesktopVideoPaddingTop: spacing.xxl,
  expandedAudioPadding: spacing.l,
};

export const collapseHeaderStyles: MediaInterpolation = {
  "audio-collapsed-desktop-landscape": {
    display: "none",
  },
  "audio-collapsed-desktop-portrait": {
    display: "none",
  },
  "audio-collapsed-mobile-landscape": {
    display: "none",
  },
  "audio-collapsed-mobile-portrait": {
    display: "none",
  },
  "audio-expanded-desktop-landscape": {
    alignItems: "flex-end",
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gridArea: "header",
    justifyContent: "center",
    zIndex: collapseHeaderVars.zIndex,
  },
  "audio-expanded-desktop-portrait": {
    alignItems: "flex-end",
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gridArea: "header",
    justifyContent: "center",
    zIndex: collapseHeaderVars.zIndex,
  },
  "audio-expanded-mobile-landscape": {
    display: "flex",
    flexDirection: "column",
    gridColumn: "collapse",
    gridRowEnd: "media-start",
    gridRowStart: "collapse-start",
    placeContent: "flex-end",
    placeItems: "center",
    position: "relative",
    width: collapseHeaderVars.mobileWidth,
    zIndex: collapseHeaderVars.zIndex,
  },
  "audio-expanded-mobile-portrait": {
    display: "flex",
    flexDirection: "column",
    gridColumn: "collapse",
    gridRowEnd: "media-start",
    gridRowStart: "collapse-start",
    placeContent: "flex-end",
    placeItems: "center",
    position: "relative",
    width: collapseHeaderVars.mobileWidth,
    zIndex: collapseHeaderVars.zIndex,
  },
  "audio-fullscreen-desktop-landscape": {},
  "audio-fullscreen-desktop-portrait": {},
  "audio-fullscreen-mobile-landscape": {},
  "audio-fullscreen-mobile-portrait": {},
  "audio-hidden-desktop-landscape": {},
  "audio-hidden-desktop-portrait": {},
  "audio-hidden-mobile-landscape": {},
  "audio-hidden-mobile-portrait": {},
  "video-collapsed-desktop-landscape": {
    display: "none",
  },
  "video-collapsed-desktop-portrait": {
    display: "none",
  },
  "video-collapsed-mobile-landscape": {
    display: "none",
  },
  "video-collapsed-mobile-portrait": {
    display: "none",
  },
  "video-expanded-desktop-landscape": {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gridArea: "header",
    justifyContent: "flex-end",
    left: collapseHeaderVars.expandedLeft,
    paddingTop: collapseHeaderVars.expandedDesktopVideoPaddingTop,
    position: "absolute",
    top: collapseHeaderVars.expandedTop,
    zIndex: collapseHeaderVars.zIndex,
  },
  "video-expanded-desktop-portrait": {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gridArea: "header",
    justifyContent: "flex-end",
    left: collapseHeaderVars.expandedLeft,
    paddingTop: collapseHeaderVars.expandedDesktopVideoPaddingTop,
    position: "absolute",
    top: collapseHeaderVars.expandedTop,
    zIndex: collapseHeaderVars.zIndex,
  },
  "video-expanded-mobile-landscape": {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gridArea: "header",
    justifyContent: "flex-end",
    left: collapseHeaderVars.expandedLeft,
    position: "absolute",
    top: collapseHeaderVars.expandedTop,
    zIndex: collapseHeaderVars.zIndex,
  },
  "video-expanded-mobile-portrait": {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap",
    gridArea: "header",
    justifyContent: "flex-end",
    left: collapseHeaderVars.expandedLeft,
    position: "absolute",
    top: collapseHeaderVars.expandedTop,
    zIndex: collapseHeaderVars.zIndex,
  },
  "video-fullscreen-desktop-landscape": {
    display: "none",
  },
  "video-fullscreen-desktop-portrait": {
    display: "none",
  },
  "video-fullscreen-mobile-landscape": {
    display: "none",
  },
  "video-fullscreen-mobile-portrait": {
    display: "none",
  },
  "video-hidden-desktop-landscape": {},
  "video-hidden-desktop-portrait": {},
  "video-hidden-mobile-landscape": {},
  "video-hidden-mobile-portrait": {},
};
//#endregion

//#region Media Element Container styles
export const mediaElementContainerVars = {
  collapsedDesktopHeight: spacing.xxxl,
  collapsedMobileHeight: spacing.xxl,
  expandedAudioSize: spacing.full,
  mobileBorderRadius: radius.none,
  videoBorderRadius: radius.none,
  videoWidth: numericViewportWidth[100],
  videoHeight: numericViewportHeight[100],
};

export const mediaElementContainerStyles: MediaInterpolation = {
  "audio-collapsed-desktop-landscape": {
    height: mediaElementContainerVars.collapsedDesktopHeight,
  },
  "audio-collapsed-desktop-portrait": {
    height: mediaElementContainerVars.collapsedDesktopHeight,
  },
  "audio-collapsed-mobile-landscape": {
    height: mediaElementContainerVars.collapsedMobileHeight,
  },
  "audio-collapsed-mobile-portrait": {
    height: mediaElementContainerVars.collapsedMobileHeight,
  },
  "audio-expanded-desktop-landscape": {
    height: mediaElementContainerVars.expandedAudioSize,
    width: mediaElementContainerVars.expandedAudioSize,
  },
  "audio-expanded-desktop-portrait": {
    height: mediaElementContainerVars.expandedAudioSize,
    width: mediaElementContainerVars.expandedAudioSize,
  },
  "audio-expanded-mobile-landscape": {
    height: mediaElementContainerVars.expandedAudioSize,
    minWidth: mediaElementContainerVars.expandedAudioSize,
  },
  "audio-expanded-mobile-portrait": {
    aspectRatio: "unset",
    borderRadius: mediaElementContainerVars.mobileBorderRadius,
    height: mediaElementContainerVars.expandedAudioSize,
    minWidth: mediaElementContainerVars.expandedAudioSize,
  },
  "audio-fullscreen-desktop-landscape": {},
  "audio-fullscreen-desktop-portrait": {},
  "audio-fullscreen-mobile-landscape": {},
  "audio-fullscreen-mobile-portrait": {},
  "audio-hidden-desktop-landscape": {
    display: "none",
  },
  "audio-hidden-desktop-portrait": {
    display: "none",
  },
  "audio-hidden-mobile-landscape": {
    display: "none",
  },
  "audio-hidden-mobile-portrait": {
    display: "none",
  },
  "video-collapsed-desktop-landscape": {
    height: mediaElementContainerVars.collapsedDesktopHeight,
  },
  "video-collapsed-desktop-portrait": {
    height: mediaElementContainerVars.collapsedDesktopHeight,
  },
  "video-collapsed-mobile-landscape": {
    height: mediaElementContainerVars.collapsedMobileHeight,
  },
  "video-collapsed-mobile-portrait": {
    height: mediaElementContainerVars.collapsedMobileHeight,
  },
  "video-expanded-desktop-landscape": {
    aspectRatio: "unset",
    borderRadius: mediaElementContainerVars.videoBorderRadius,
    height: mediaElementContainerVars.videoHeight,
    width: mediaElementContainerVars.videoWidth,
  },
  "video-expanded-desktop-portrait": {
    aspectRatio: "unset",
    borderRadius: mediaElementContainerVars.videoBorderRadius,
    height: mediaElementContainerVars.videoHeight,
    width: mediaElementContainerVars.videoWidth,
  },
  "video-expanded-mobile-landscape": {
    aspectRatio: "unset",
    borderRadius: mediaElementContainerVars.videoBorderRadius,
    height: mediaElementContainerVars.videoHeight,
    width: mediaElementContainerVars.videoWidth,
  },
  "video-expanded-mobile-portrait": {
    aspectRatio: "unset",
    borderRadius: mediaElementContainerVars.videoBorderRadius,
    height: mediaElementContainerVars.videoHeight,
    width: mediaElementContainerVars.videoWidth,
  },
  "video-fullscreen-desktop-landscape": {
    aspectRatio: "unset",
    borderRadius: mediaElementContainerVars.videoBorderRadius,
    height: mediaElementContainerVars.videoHeight,
    width: mediaElementContainerVars.videoWidth,
  },
  "video-fullscreen-desktop-portrait": {
    aspectRatio: "unset",
    borderRadius: mediaElementContainerVars.videoBorderRadius,
    height: mediaElementContainerVars.videoHeight,
    width: mediaElementContainerVars.videoWidth,
  },
  "video-fullscreen-mobile-landscape": {
    aspectRatio: "unset",
    borderRadius: mediaElementContainerVars.videoBorderRadius,
    height: mediaElementContainerVars.videoHeight,
    width: mediaElementContainerVars.videoWidth,
  },
  "video-fullscreen-mobile-portrait": {
    aspectRatio: "unset",
    borderRadius: mediaElementContainerVars.videoBorderRadius,
    height: mediaElementContainerVars.videoHeight,
    width: mediaElementContainerVars.videoWidth,
  },
  "video-hidden-desktop-landscape": {
    display: "none",
  },
  "video-hidden-desktop-portrait": {
    display: "none",
  },
  "video-hidden-mobile-landscape": {
    display: "none",
  },
  "video-hidden-mobile-portrait": {
    display: "none",
  },
};
//#endregion

//#region Subtitles (with controls)
export const subtitlesVars = {
  smallTransform: "translate(-50%, -10vh)",
  largeTransform: "translate(-50%, -35vh)",
};

export const subtitlesWithControls: MediaInterpolation = {
  "audio-collapsed-desktop-landscape": {},
  "audio-collapsed-desktop-portrait": {},
  "audio-collapsed-mobile-landscape": {},
  "audio-collapsed-mobile-portrait": {},
  "audio-expanded-desktop-landscape": {},
  "audio-expanded-desktop-portrait": {},
  "audio-expanded-mobile-landscape": {},
  "audio-expanded-mobile-portrait": {},
  "audio-fullscreen-desktop-landscape": {},
  "audio-fullscreen-desktop-portrait": {},
  "audio-fullscreen-mobile-landscape": {},
  "audio-fullscreen-mobile-portrait": {},
  "audio-hidden-desktop-landscape": {},
  "audio-hidden-desktop-portrait": {},
  "audio-hidden-mobile-landscape": {},
  "audio-hidden-mobile-portrait": {},
  "video-collapsed-desktop-landscape": {},
  "video-collapsed-desktop-portrait": {},
  "video-collapsed-mobile-landscape": {},
  "video-collapsed-mobile-portrait": {},
  "video-expanded-desktop-landscape": {
    transform: subtitlesVars.smallTransform,
  },
  "video-expanded-desktop-portrait": {
    transform: subtitlesVars.smallTransform,
  },
  "video-expanded-mobile-landscape": {},
  "video-expanded-mobile-portrait": {
    transform: subtitlesVars.largeTransform,
  },
  "video-fullscreen-desktop-landscape": {
    transform: subtitlesVars.smallTransform,
  },
  "video-fullscreen-desktop-portrait": {
    transform: subtitlesVars.smallTransform,
  },
  "video-fullscreen-mobile-landscape": {},
  "video-fullscreen-mobile-portrait": {
    transform: subtitlesVars.largeTransform,
  },
  "video-hidden-desktop-landscape": {},
  "video-hidden-desktop-portrait": {},
  "video-hidden-mobile-landscape": {},
  "video-hidden-mobile-portrait": {},
};
//#endregion
